import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { channelPartnerApi } from "../../api/client";
import { ChevronLeft, Eye, EyeOff } from "lucide-react";

const CreateChannelPartner = () => {
  const navigate = useNavigate();
  const form = useRef();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    const formData = new FormData(form.current);

    // Validation
    const name = formData.get("name")?.trim();
    const email = formData.get("email")?.trim();
    const phoneNumber = formData.get("phoneNumber")?.trim();
    const password = formData.get("password")?.trim();

    if (!name) return setError("Name is required");
    if (!isValidEmail(email)) return setError("Valid email is required");
    if (!phoneNumber || phoneNumber.length !== 10)
      return setError("Valid 10-digit phone number is required");
    if (!password || password.length < 8)
      return setError("Password must be at least 8 characters");

    // Check password complexity
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password))
      return setError(
        "Password must contain at least one letter and one number"
      );

    setLoading(true);
    try {
      const submissionData = {
        name,
        email,
        password,
        phoneNumber: `+91${phoneNumber}`, // Format phone number properly
      };

      const response = await channelPartnerApi.registerSimple(submissionData);

      if (response) {
        setMessage(
          "✅ Channel Partner created successfully! They can now login and complete their profile."
        );
        form.current.reset();
        // Navigate back to dashboard after 2 seconds
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      }
    } catch (error) {
      console.error("Create channel partner error:", error);
      console.error("Error details:", {
        status: error?.status,
        data: error?.data,
        message: error?.message,
      });

      // Better error handling
      let errorMessage = "Failed to create channel partner. Please try again.";

      if (error?.data?.code === "CONFLICT") {
        errorMessage =
          "Email already exists. Please use a different email address.";
      } else if (error?.data?.code === "VALIDATION_ERROR") {
        errorMessage =
          error.data.errors?.[0]?.message ||
          "Validation error. Please check your input.";
      } else if (error?.data?.code === "INTERNAL_ERROR") {
        errorMessage =
          "Server error occurred. Please try again or contact support if the problem persists.";
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          Create Channel Partner
        </h1>
        <p className="text-gray-600 mt-2">
          Create a new channel partner account. They will complete their profile
          after login.
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}
      {message && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          {message}
        </div>
      )}

      {/* Form */}
      <form
        ref={form}
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-lg p-8 space-y-6"
      >
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                name="name"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter email address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phoneNumber"
                maxLength={10}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="10-digit phone number (e.g., 9876543210)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter 10-digit Indian mobile number (will be automatically
                formatted as +91xxxxxxxxxx)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Minimum 8 characters (letters + numbers)"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 text-sm">
            <strong>Note:</strong> The channel partner will be able to complete
            their business information, upload documents, and set up their
            profile after logging in.
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              loading
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {loading ? "Creating..." : "Create Channel Partner"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateChannelPartner;
